import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { PiWarning } from 'react-icons/pi';
import type { VerificationInFlightBody } from '@/models/offers';

const MIN_REASON = 3;

/**
 * The way past a verification that is blocking a paying-party change.
 *
 * Ending somebody's verification mid-flight is a real cost to the customer, so
 * this is deliberately a stop rather than a confirm: the reason is required and
 * is written into the audit entry beside the change it explains. Staff without
 * the privilege are told what is holding the change rather than being offered a
 * button that would only fail.
 */
export default function CancelVerificationModal({
  conflict,
  busy,
  onConfirm,
  onClose,
}: {
  conflict: VerificationInFlightBody | null;
  busy?: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (conflict) {
      setReason('');
      setTouched(false);
    }
  }, [conflict]);

  const trimmed = reason.trim();
  const tooShort = trimmed.length < MIN_REASON;

  return (
    <Modal
      opened={!!conflict}
      onClose={onClose}
      title="A verification is in progress"
      size="lg"
    >
      {conflict && (
        <Stack gap="md">
          <Alert color="orange" icon={<PiWarning size={16} />}>
            <Text fz="sm">{conflict.error}</Text>
          </Alert>

          {conflict.canOverride ? (
            <>
              <Text fz="sm" c="dimmed">
                {conflict.boundToCurrentPayer
                  ? 'Continuing ends the verification the customer is part way through. They will have to start again from the beginning.'
                  : 'Continuing ends the verification the customer is part way through for this offer. Any progress they have made is discarded and they will have to start again.'}
              </Text>
              <Textarea
                label="Why is this necessary?"
                description="Recorded against the offer alongside the change."
                placeholder="e.g. customer asked us to bill their holding company instead"
                autosize
                minRows={2}
                value={reason}
                onChange={(e) => setReason(e.currentTarget.value)}
                onBlur={() => setTouched(true)}
                error={touched && tooShort ? 'Give a short reason.' : undefined}
              />
              <Group justify="flex-end">
                <Button variant="default" onClick={onClose} disabled={busy}>
                  Leave it running
                </Button>
                <Button
                  color="red"
                  loading={busy}
                  onClick={() => {
                    setTouched(true);
                    if (!tooShort) onConfirm(trimmed);
                  }}
                  disabled={tooShort}
                >
                  Cancel verification and continue
                </Button>
              </Group>
            </>
          ) : (
            <>
              <Text fz="sm" c="dimmed">
                Ending a verification in progress needs wider payer access. Ask
                someone with it to cancel the verification, then try again.
              </Text>
              <Group justify="flex-end">
                <Button variant="default" onClick={onClose}>
                  Close
                </Button>
              </Group>
            </>
          )}
        </Stack>
      )}
    </Modal>
  );
}
